/**
 * Content for the marketing feature pages. Two kinds of export live here:
 *
 *   - Nine bespoke pages, each with its own component: ITINERARY_PLANNER, AI_TRAVEL_PLANNER,
 *     BUDGET_PLANNER, PACKING_LIST, PHOTOBOOK, TRAVEL_TRACKER, TRAVEL_REEL, ESIM and PRO
 *     (/pro renders B2CPricingPage). Their copy fields are DERIVED, see below.
 *   - The generic pages rendered through components/website/pages/FeaturePage.tsx
 *     (/expense-tracker, /your-map, /social-travel-platform, /travel-journal, /scratch-map).
 *     Edited here, as before.
 *
 * DERIVED, for the nine bespoke exports: seoTitle, seoDescription, pageTitle,
 * softwareApplication.{name,description}, howToUse.{name,description}, howToSteps[].{name,text}
 * and faqItems[].{question,answer} are written from website/feature-pages/<page>.md in the
 * stippl-knowledge repo by `python3 tools/build_feature_pages.py <page>` (123yb6946ny). Change
 * that copy in the markdown, not here: the next build stops on a direct edit to any of these
 * fields and names it as drift. Everything else in those exports (images, title/text,
 * operatingSystem, downloadUrl, featureList) is still edited here.
 *
 * marketingRoutes.tsx (the SSR <title>, meta and JSON-LD) and the page components both import
 * these objects.
 */

import { BudgetTrackerFeaturesContent } from "components/website/content/features/BudgetTrackerFeatures";
import { PlacesFeaturesContent } from "components/website/content/features/PlacesFeature";
import { ScratchMapFeatureContent } from "components/website/content/features/ScratchMapFeature";
import { SocialFeaturesContent } from "components/website/content/features/SocialFeature";
import { TravelJournalFeatureContent } from "components/website/content/features/TravelJournalFeature";
import BudgetHeaderMobile from "components/website/img/screenshots/budgetplanner/budget-visual-header-mobile.png";
import BudgetHeaderDesktop from "components/website/img/screenshots/budgetplanner/budget-visual-header.png";
import JournalHeaderDesktop from "components/website/img/screenshots/journals/journal-header.png";
import JournalHeaderMobile from "components/website/img/screenshots/journals/journal-mobile-header.png";
import PlacesHeaderMobile from "components/website/img/screenshots/places/places-visual-header-mobile.png";
import PlacesHeaderDesktop from "components/website/img/screenshots/places/places-visual-header.png";
import SocialHeaderMobile from "components/website/img/screenshots/profile/profile-header-mobile.png";
import SocialHeaderDesktop from "components/website/img/screenshots/profile/profile-header.png";
import ScratchmapHeaderMobile from "components/website/img/screenshots/scratchmap/scratchmap-visual-header-mobile.png";
import ScratchmapHeaderDesktop from "components/website/img/screenshots/scratchmap/scratchmap-visual-header.png";

export const ITINERARY_PLANNER_CONTENT = {
	// seoTitle/seoDescription are the <title> and meta description, and deliberately not title/text:
	// this page's H1 comes from its i18n namespace, and title/text are only buildHead's fallback. One
	// value serving both roles is how /packing-list fell from 36,126 impressions to 661 (123yb692vrt).
	// Derived from the knowledge base — see the header.
	seoTitle: "The itinerary planner for trips worth remembering",
	seoDescription:
		"Build a beautiful day-by-day travel itinerary: with your route on a map, your stays and activities all in one place. Free forever, for any trip.",
	title: "The itinerary planner for trips worth remembering",
	text: "Build a beautiful day-by-day travel itinerary: with your route on a map, your stays and activities all in one place. Free forever, for any trip.",
	pageTitle: "Itinerary Planner",
	softwareApplication: {
		name: "Stippl Itinerary Planner",
		description:
			"All-in-one trip planning app for travellers. Build a day-by-day itinerary with route, accommodation, transport, places to visit and shared notes.",
	},
	howToUse: {
		name: "How to build a travel itinerary with Stippl",
		description: "From blank page to full itinerary in minutes. Here's how it works.",
	},
	howToSteps: [
		{ name: "Pick your destination", text: "Sign up free and drop in where you're going. Your adventure starts here." },
		{ name: "Plot your route", text: "Add your stops and watch your route appear on the map automatically." },
		{ name: "Build your day-by-day itinerary", text: "Add stays, things to do, restaurants and how you're getting there." },
		{ name: "Bring your crew", text: "Invite friends or family: plan together, zero group chat chaos." },
		{ name: "Go explore", text: "Open the app and go. Your whole trip in your pocket, even offline." },
	],
	faqItems: [
		{
			question: "Is Stippl's itinerary planner free?",
			answer:
				"Yes, completely free. Build full day-by-day travel itineraries at no cost, invite travel partners and manage your entire trip through the Stippl app on web, iOS and Android. No hidden fees, no premium paywall for the core planner.",
		},
		{
			question: "Can I use Stippl as a holiday planner or vacation planner?",
			answer:
				"Absolutely. Stippl works for any kind of trip: city breaks, long-haul holidays, family vacations or backpacking adventures. Add your destinations, build a day-by-day plan with accommodation, activities and transport, and share it with everyone you're travelling with.",
		},
		{
			question: "Does Stippl have an AI travel planner?",
			answer:
				"Yes. Stippl's AI travel planner generates a personalised day-by-day itinerary in seconds based on your destination, trip length and travel style. The generated plan lands directly in your itinerary, ready to edit, share or take offline.",
		},
		{
			question: "What makes Stippl different from other travel itinerary planners?",
			answer:
				"Stippl combines route mapping, day-by-day planning, accommodation search, transport tracking and group collaboration in one free app. Everything is in one place, on web and mobile, from your first destination to your last restaurant booking.",
		},
		{
			question: "Can I share my itinerary with travel companions?",
			answer:
				"Yes. Invite friends or family to your trip and plan together in real time. Everyone can view, suggest and edit, no more endless group chats or version chaos.",
		},
		{
			question: "Does Stippl work offline?",
			answer:
				"Yes: the Stippl mobile app saves your full itinerary to your device so you can access every stop, note and booking without Wi-Fi. Perfect for when you're exploring somewhere new with no data.",
		},
	],
};

export const AI_TRAVEL_PLANNER_CONTENT = {
	// seoTitle/seoDescription are the <title> and meta description, and deliberately not title/text:
	// this page's H1 comes from its i18n namespace, and title/text are only buildHead's fallback. One
	// value serving both roles is how /packing-list fell from 36,126 impressions to 661 (123yb692vrt).
	// Derived from the knowledge base — see the header.
	seoTitle: "The AI travel planner you actually use on the trip",
	seoDescription:
		"Most AI travel tools give you a text response. Stippl gives you a structured, editable, shareable day-by-day itinerary: connected to your budget, your packing list, and your group. Not just a plan. A trip.",
	title: "The AI travel planner you actually use on the trip",
	text: "Most AI travel tools give you a text response. Stippl gives you a structured, editable, shareable day-by-day itinerary: connected to your budget, your packing list, and your group. Not just a plan. A trip.",
	pageTitle: "AI Travel Planner",
	softwareApplication: {
		name: "Stippl AI Travel Planner",
		description: "AI-powered trip planning. Generate complete personalised itineraries in minutes, then customise every part of the plan.",
	},
	howToUse: {
		name: "From idea to itinerary in 3 steps",
		description: "No more browser tabs. No more spreadsheets. Just describe your trip and let the AI handle the details.",
	},
	howToSteps: [
		{
			name: "Describe your trip",
			text: "Tell the AI your destination, travel dates and any specific preferences like budget or diet. Then pick a trip style: relaxation, culture, adventure or off the beaten track. The more you share, the better the plan.",
		},
		{
			name: "Get your itinerary",
			text: "In under 2 minutes, your AI travel planner generates a full day-by-day plan with activities and accommodation suggestions for every destination on your trip.",
		},
		{
			name: "Customise & live in it",
			text: "Edit any detail, swap activities, add your own favourite spots. Share a live link with travel companions. Track your budget, check off your packing list, and journal your trip, all in the same app.",
		},
	],
	faqItems: [
		{
			question: "What is an AI travel planner?",
			answer:
				"An AI travel planner generates a personalised, day-by-day travel itinerary based on your destination, dates and preferences, typically in under 5 minutes. Instead of spending hours researching across multiple tabs, you describe your trip and the AI creates a complete plan covering activities, restaurants, transport and accommodation. Stippl's AI trip planner goes further: it connects that generated plan directly to a budget tracker, packing list, route map and travel journal, all in one free app.",
		},
		{
			question: "Is Stippl's AI travel planner free?",
			answer:
				"Yes, for your first two trips. Every account gets 2 free AI-generated itineraries and Stippl Pro makes them unlimited. The full trip planning app: itinerary builder, budget tracker, packing list, travel journal and travel tracker, is free to use with no route-length limits or hidden paywalls.",
		},
		{
			question: "What is the best AI travel planner in 2026?",
			answer:
				"The best AI travel planner is one that does not stop at generating a plan. Stippl generates your full route and day-by-day itinerary in under 2 minutes: then gives you a structured, editable trip you can actually use. Share it live with your group, track your budget, manage your packing list and journal the trip, all in the same app.",
		},
		{
			question: "How is Stippl different from Layla or Mindtrip?",
			answer:
				"Layla and Mindtrip are conversational AI trip planners that generate itinerary suggestions via a chat interface. Stippl generates structured, editable, shareable day-by-day itineraries that connect directly to a budget planner, packing list, live map tracker and travel journal. Layla and Mindtrip give you a plan in a chat window, Stippl is the app where you build and live in the trip.",
		},
		{
			question: "Is Stippl's AI trip planner better than ChatGPT for planning a trip?",
			answer:
				"For building a usable, shareable trip plan, yes. ChatGPT generates travel suggestions as text in a chat window with no way to share collaboratively, track your budget, or mark off a packing list. Stippl's AI generates a structured day-by-day itinerary that connects to maps, a budget planner, packing lists and real-time group sharing. Use ChatGPT for travel inspiration, use Stippl to actually build and manage the trip.",
		},
		{
			question: "When is the AI Chat feature coming?",
			answer:
				"The AI Chat companion is currently in development. It will allow you to have a conversation with an AI that knows your full itinerary: ask questions, request changes and get real-time recommendations while you're travelling. Pro subscribers get early access first.",
		},
	],
};

export const CHATGPT_TRAVEL_PLANNER_CONTENT = {
	// Hand-written, not derived from stippl-knowledge: the ChatGPT and Claude landing pages are one component
	// (AssistantTravelPlannerPage) serving two routes, and the copy comes from the designer's final page. The
	// page reads its FAQ and how-to text from the `assistantTravelPlanner` i18n namespace and falls back to
	// these English values, as AiTravelPlannerPage does for its own namespace.
	seoTitle: "ChatGPT Travel Planner | Stippl",
	seoDescription:
		"Plan your trip in ChatGPT, then save it to Stippl: a route on the map, dated days and real places. Plan together, book stays, budget, pack and relive it.",
	title: "Turn your ChatGPT travel plan into a real trip",
	text: "Dream it up in ChatGPT. Say “save it to Stippl”. Get a real trip: on the map, day by day, ready to go.",
	pageTitle: "ChatGPT Travel Planner",
	softwareApplication: {
		name: "Stippl for ChatGPT",
		description:
			"Plan your trip in ChatGPT, then save it to Stippl: a route on the map, dated days and real places. Plan together, book stays, budget, pack and relive it.",
		featureList: ["Save a trip from ChatGPT", "See your Stippl trips", "Add places to a day", "Replace a day", "Turn a day into a day trip"],
	},
	howToUse: {
		name: "How to add Stippl to ChatGPT",
		description: "Once. Then just say “save it to Stippl”.",
	},
	howToSteps: [
		{ name: "Open Plugins", text: "In ChatGPT, open Plugins and search for Stippl Travel Planner." },
		{ name: "Add Stippl", text: "Open Stippl Travel Planner and add it to ChatGPT." },
		{ name: "Allow access", text: "Sign in to Stippl or create an account, then click Allow." },
		{ name: "Save a trip", text: "Plan a trip in ChatGPT, type @, pick Stippl and say: save it to Stippl." },
	],
	faqItems: [
		{
			question: "Can ChatGPT plan a trip and save it as an itinerary?",
			answer:
				"Yes. Add Stippl to ChatGPT, plan your trip in the chat, then type @, pick Stippl and say “save it to Stippl”. ChatGPT sends the plan to your Stippl account, where it becomes a trip with dated days, a route on the map and an address and photos for every place.",
		},
		{
			question: "Why not just keep my itinerary in ChatGPT?",
			answer:
				"A chat is great for ideas, but the plan stays text. In Stippl the same plan becomes a trip you can change on the go, plan with friends, book stays for, budget, pack for, journal and turn into a photobook.",
		},
		{
			question: "How do I add Stippl to ChatGPT?",
			answer:
				"In ChatGPT, open Plugins, search for Stippl Travel Planner and add it. Sign in to Stippl or create an account, click Allow, and you're done. You only do this once.",
		},
		{
			question: "Can Stippl read my ChatGPT conversations?",
			answer:
				"No. Stippl only receives what ChatGPT sends to save or change your trip: the trip name, start date, destinations, and the places, times and notes for each day. We never see your conversation.",
		},
		{
			question: "What can I change from ChatGPT?",
			answer:
				"You can save new trips, list your Stippl trips, add places to a day, replace a day (after you confirm) and turn a day into a day trip. Everything else, like stays, budget and packing, you do in the Stippl app or on stippl.io.",
		},
		{
			question: "Can ChatGPT book hotels through Stippl?",
			answer:
				"No. ChatGPT can't book or pay for anything. Once your trip is in Stippl, you can compare and book a stay for every stop, with your dates already filled in.",
		},
		{
			question: "How long does the connection last?",
			answer:
				"You connect once. The connection ends if you disconnect, change your Stippl password, delete your account, don't use it for 90 days, or after 365 days at most. You can reconnect at any time.",
		},
	],
};

export const CLAUDE_TRAVEL_PLANNER_CONTENT = {
	// Hand-written, not derived from stippl-knowledge: the ChatGPT and Claude landing pages are one component
	// (AssistantTravelPlannerPage) serving two routes, and the copy comes from the designer's final page. The
	// page reads its FAQ and how-to text from the `assistantTravelPlanner` i18n namespace and falls back to
	// these English values, as AiTravelPlannerPage does for its own namespace.
	seoTitle: "Claude Travel Planner | Stippl",
	seoDescription:
		"Plan your trip in Claude, then save it to Stippl: a route on the map, dated days and real places. Plan together, book stays, budget, pack and relive it.",
	title: "Turn your Claude travel plan into a real trip",
	text: "Dream it up in Claude. Say “save it to Stippl”. Get a real trip: on the map, day by day, ready to go.",
	pageTitle: "Claude Travel Planner",
	softwareApplication: {
		name: "Stippl for Claude",
		description:
			"Plan your trip in Claude, then save it to Stippl: a route on the map, dated days and real places. Plan together, book stays, budget, pack and relive it.",
		featureList: ["Save a trip from Claude", "See your Stippl trips", "Add places to a day", "Replace a day", "Turn a day into a day trip"],
	},
	howToUse: {
		name: "How to add Stippl to Claude",
		description: "Once. Then just say “save it to Stippl”.",
	},
	howToSteps: [
		{ name: "Open Connectors", text: "In Claude, go to Customize, then Connectors, and click +." },
		{ name: "Install and connect", text: "Find Stippl and click Connect." },
		{ name: "Allow access", text: "Sign in to Stippl or create an account, then click Allow." },
		{ name: "Save a trip", text: "Plan a trip in Claude and say: save it to Stippl." },
	],
	faqItems: [
		{
			question: "Can Claude plan a trip and save it as an itinerary?",
			answer:
				"Yes. Add Stippl to Claude, plan your trip in the chat and end with “save it to Stippl”. Claude sends the plan to your Stippl account, where it becomes a trip with dated days, a route on the map and an address and photos for every place.",
		},
		{
			question: "Why not just keep my itinerary in Claude?",
			answer:
				"A chat is great for ideas, but the plan stays text. In Stippl the same plan becomes a trip you can change on the go, plan with friends, book stays for, budget, pack for, journal and turn into a photobook.",
		},
		{
			question: "How do I add Stippl to Claude?",
			answer:
				"Open Customize, then Connectors in Claude, click + and connect Stippl. Sign in to Stippl or create an account, click Allow, and you're done. You only do this once.",
		},
		{
			question: "Can Stippl read my Claude conversations?",
			answer:
				"No. Stippl only receives what Claude sends to save or change your trip: the trip name, start date, destinations, and the places, times and notes for each day. We never see your conversation.",
		},
		{
			question: "What can I change from Claude?",
			answer:
				"You can save new trips, list your Stippl trips, add places to a day, replace a day (after you confirm) and turn a day into a day trip. Everything else, like stays, budget and packing, you do in the Stippl app or on stippl.io.",
		},
		{
			question: "Can Claude book hotels through Stippl?",
			answer:
				"No. Claude can't book or pay for anything. Once your trip is in Stippl, you can compare and book a stay for every stop, with your dates already filled in.",
		},
		{
			question: "How long does the connection last?",
			answer:
				"You connect once. The connection ends if you disconnect, change your Stippl password, delete your account, don't use it for 90 days, or after 365 days at most. You can reconnect at any time.",
		},
	],
};

export const BUDGET_PLANNER_CONTENT = {
	// seoTitle/seoDescription are the <title> and meta description, and deliberately not title/text:
	// this page's H1 comes from its i18n namespace, and title/text are only buildHead's fallback. One
	// value serving both roles is how /packing-list fell from 36,126 impressions to 661 (123yb692vrt).
	// Derived from the knowledge base — see the header.
	seoTitle: "Free travel budget planner. Know exactly where your money goes.",
	seoDescription: "Stippl is a free trip planner, with a built-in expense tracker so you always know where your holiday money went.",
	title: "Free travel budget planner. Know exactly where your money goes.",
	text: "Stippl is a free trip planner, with a built-in expense tracker so you always know where your holiday money went.",
	pageTitle: "Budget Planner",
	softwareApplication: {
		name: "Stippl Travel Budget Planner",
		description: "Free travel budget planner and expense tracker with group cost splitting and 100+ currency conversion.",
	},
	howToUse: {
		name: "How to use Stippl as your travel budget planner",
		description: "From setting your trip budget to the final settlement, here's how the trip budget planner works.",
	},
	howToSteps: [
		{ name: "Create your trip", text: "Start a trip in Stippl, give it a name and invite your travel partners." },
		{ name: "Log expenses as you go", text: "Add costs by category in any currency, directly from your phone on the road." },
		{ name: "Split shared costs", text: "Mark who paid what. Stippl calculates the simplest way for everyone to settle." },
		{ name: "Review & export", text: "When you're back home, export your full expense list to CSV for reimbursements or personal records." },
	],
	faqItems: [
		{
			question: "Is Stippl's travel expense tracker free?",
			answer:
				"Yes, everything you need is free. Track expenses, split costs with co-travellers and convert 100+ currencies at no cost, on web, iOS and Android. Stippl Pro (from €4.99/month) adds extras like CSV export and custom categories, but most travellers never need it.",
		},
		{
			question: "Can I split travel expenses with friends or family?",
			answer:
				"Yes. Add shared costs, mark who paid, and Stippl works out who owes what, using the fewest transfers possible to settle up. Works for any group size, and you can split evenly or assign custom amounts per person.",
		},
		{
			question: "Does Stippl support multiple currencies?",
			answer:
				"Yes, 100+ currencies with automatic conversion. Log expenses in the local currency at each destination and your total always shows in your home currency. Perfect for multi-country trips.",
		},
		{
			question: "Can I export my travel expenses?",
			answer: "Yes, with Stippl Pro. Download your full trip expense list as a CSV, handy for employer reimbursements or keeping your own records.",
		},
		{
			question: "Does it work offline?",
			answer:
				"Partly. The expenses you have already logged stay visible without a connection, but adding a new expense or switching currency needs you back online.",
		},
	],
};

export const EXPENSE_TRACKER_CONTENT = {
	relatedFeatures: [
		{ label: "Budget planner", href: "/budget-planner", icon: "coin", color: "main3" },
		{ label: "Itinerary planner", href: "/itinerary-planner", icon: "calendar", color: "main1" },
	],
	title: "The best way to track expenses throughout your travels",
	text: "Track your expenses and optimize them while you travel",
	cta: "Explore budget planner",
	icon: "coin",
	feature: "Expense tracker",
	usps: [
		"Add & categorise expenses on the go",
		"Visualize spendings with graphs to see on what you are spending the most",
		"Split & settle expenses among co-travelers",
		"Convert currencies when you change countries",
	],
	color: "main3",
	type: "budget",
	featureList: BudgetTrackerFeaturesContent,
	headerImage: [BudgetHeaderDesktop, BudgetHeaderMobile],
	pageTitle: "Expense Tracker",
};

export const PACKING_LIST_CONTENT = {
	// seoTitle/seoDescription are the <title> and meta description, and deliberately not title/text:
	// this page's H1 comes from its i18n namespace, and title/text are only buildHead's fallback. One
	// value serving both roles is how /packing-list fell from 36,126 impressions to 661 (123yb692vrt).
	// Derived from the knowledge base — see the header.
	seoTitle: "Packing list maker: pack any trip, free | Stippl",
	seoDescription:
		"Build your packing list inside your trip: add items, check them off as you pack, right next to your itinerary and budget. Free, on web, iOS and Android.",
	title: "Never forget a thing. Pack any trip in minutes.",
	text: "Stippl's built-in packing list gives you pre-made templates for any trip type, shared group checklists and item assignment, so nothing gets forgotten and nothing gets packed twice. All alongside your itinerary and budget.",
	pageTitle: "Packing List",
	softwareApplication: {
		name: "Stippl Packing List",
		description: "Free travel packing list app with pre-built templates, shared group checklists and item assignment.",
	},
	howToUse: {
		name: "How to create a travel packing list with Stippl",
		description: "From choosing a template to checking off the last item, here's how the packing list app works.",
	},
	howToSteps: [
		{ name: "Create your trip", text: "Start a trip in Stippl, the packing list is already built in alongside your itinerary and budget." },
		{ name: "Choose a template", text: "Pick a pre-built packing list template for your trip type, or create a custom list from scratch." },
		{ name: "Share with your group", text: "Invite your travel partners to the shared packing list. Assign items so everyone knows what to bring." },
		{ name: "Pack and check off", text: "Check items off on your phone as you pack. Everything syncs across the group in real time." },
	],
	faqItems: [
		{
			question: "Is Stippl's travel packing list free?",
			answer:
				"Yes, completely free. Build custom checklists, use pre-made packing list templates, share lists with your group and assign items to specific people, on web, iOS and Android. No hidden fees, no paywall.",
		},
		{
			question: "Can I share a packing list with my travel group?",
			answer:
				"Yes. Stippl is a free shared packing list app. Create a group packing list, invite your travel partners, and everyone can see items, check things off and add their own, in real time from any device. You can also assign specific items to specific people so there's no confusion about who is bringing what.",
		},
		{
			question: "Does Stippl have pre-made packing list templates?",
			answer:
				"Yes. Stippl includes pre-built travel packing list templates for beach holidays, city breaks, hiking trips, business travel and backpacking. Start from a template, remove what doesn't apply and add anything specific to your trip. You can also build a fully custom packing list from scratch.",
		},
		{
			question: "Can I reuse a packing list for future trips?",
			answer:
				"Yes. Packing lists are saved inside your Stippl trips and can be copied and adapted for future travel. You don't need to rebuild your trip packing list from scratch every time you plan a new holiday.",
		},
		{
			question: "What's the difference between a travel packing list and a trip packing list?",
			answer:
				"Same thing: whether you call it a travel packing list, trip packing list, holiday packing list, shared packing list or packing list maker, Stippl covers all of it. The packing list app is built directly into your trip alongside your itinerary and budget planner, so everything stays in one place.",
		},
	],
};

export const YOUR_MAP_CONTENT = {
	relatedFeatures: [
		{ label: "Itinerary planner", href: "/itinerary-planner", icon: "calendar", color: "main1" },
		{ label: "Travel tracker", href: "/travel-tracker", icon: "globe", color: "main2" },
		{ label: "Scratch map", href: "/scratch-map", icon: "scratchGlobe", color: "main3" },
	],
	title: "Pin all your places on your map",
	text: "Save your favourite spots on each location, create your bucket list and share your map with friends.",
	cta: "Explore your map",
	usps: [
		"Save articles, places to sleep & where to eat & drink",
		"One overview per location of the places you recommend (or not)",
		"Share your tips and recommendations with friends",
	],
	color: "main4",
	type: "places",
	featureList: PlacesFeaturesContent,
	headerImage: [PlacesHeaderDesktop, PlacesHeaderMobile],
	pageTitle: "Your map",
	textSection: {
		heading: "What is a personal travel map?",
		paragraphs: [
			"A personal travel map is a digital map where you save the places that matter to you: places you've visited, restaurants you love, hidden spots you've discovered and destinations you want to explore next. Instead of scattering recommendations across notes apps, browser bookmarks and WhatsApp messages, a travel map puts everything on one interactive layer you can actually use.",
			"Stippl's travel map is built around destinations, not just points. For every city or location, you can build a curated collection of places: where to eat, where to stay, what to see, what to skip. Add personal notes to each spot so future-you (or your friends) know exactly why it's worth visiting. Your map becomes a living travel guide shaped by your own experience.",
			"Sharing is built into the core of the feature. Send your map to a friend planning the same destination and they get instant access to your recommendations: no copying, no screenshots, no 'I'll send you the link later'. You can also follow other travellers on Stippl and discover their maps, which turns the whole platform into a peer-sourced travel guide built on real experience rather than sponsored rankings.",
			"Your map connects directly to your trip itineraries. Places saved on your map can be pulled into any trip you're planning, so scouting and planning happen in the same tool. Bucket-list destinations go from wishlist to itinerary in a single step.",
		],
	},
	faqItems: [
		{
			question: "Is Stippl's travel map free?",
			answer: "Yes. Saving places, building your personal travel map and sharing it with friends is completely free in Stippl on web, iOS and Android.",
		},
		{
			question: "Can I share my travel map with friends?",
			answer:
				"Yes. Share your map directly with friends so they can browse your recommendations for any destination. Your map works as a personal travel guide other people can actually use.",
		},
		{
			question: "Can I save a bucket list on my travel map?",
			answer:
				"Yes. Mark destinations as places you want to visit and they appear as wishlist spots on your map. Your bucket list and your visited places live side by side so you always know what's next.",
		},
		{
			question: "Does the travel map work with my trip itineraries?",
			answer:
				"Yes. Places saved on your map can be added directly to any trip itinerary in Stippl. Discover a great restaurant while browsing your map and drop it into your upcoming trip in one step.",
		},
		{
			question: "Does Stippl's travel map work on mobile?",
			answer:
				"Yes. Your personal travel map is fully accessible on iOS and Android. Browse your saved places, add new spots and share recommendations from your phone.",
		},
	],
};

export const SOCIAL_TRAVEL_PLATFORM_CONTENT = {
	relatedFeatures: [
		{ label: "Travel journal", href: "/travel-journal", icon: "addPhoto", color: "main4" },
		{ label: "Travel reel", href: "/travel-reel", icon: "movie", color: "main4" },
		{ label: "Scratch map", href: "/scratch-map", icon: "scratchGlobe", color: "main1" },
	],
	title: "The social media platform for travelers",
	text: "Connect with friends and like-minded travelers and share your adventures",
	cta: "Explore the platform",
	usps: [
		"Follow your friends and other travelers you admire",
		"Save and recommend the places you love for each destination",
		"Add places to your wishlist for each destination",
		"Interact with your followers",
	],
	color: "main1",
	icon: "userGroup",
	feature: "Travel profile",
	featureList: SocialFeaturesContent,
	headerImage: [SocialHeaderDesktop, SocialHeaderMobile],
	pageTitle: "Stippl Travel Profile",
	textSection: {
		heading: "What is a social travel platform?",
		paragraphs: [
			"A social travel platform is a place where travellers share trips, recommendations and experiences with each other: not as polished highlights-only posts, but as real travel knowledge that's actually useful to other people planning the same places. It sits between a social network and a travel guide: the content comes from real travellers, the structure makes it searchable and the connections make it personal.",
			"Stippl's travel community is built around the places you've been and the trips you've taken. Your profile shows your travel history, your maps and your journals. Follow friends and fellow travellers to see where they've been, what they recommend and where they're going next. The discovery is peer-sourced: you're getting recommendations from people whose taste you know, not anonymous reviews from strangers.",
			"Sharing on Stippl isn't just posting photos. You share the full trip: the itinerary, the places you loved, the restaurants worth the queue and the ones to skip. Other travellers can browse your published trips and use them as inspiration or a starting point for their own planning. Your experiences become genuinely useful to people planning the same destinations.",
			"The social layer connects to everything else in Stippl. Your map of favourite spots, your travel journals and your scratch map are all visible on your profile. Follow someone interesting and you get a window into their whole travel life, not just the photos they chose to post.",
		],
	},
	faqItems: [
		{
			question: "Is Stippl's travel community free?",
			answer:
				"Yes. Creating a profile, following other travellers, sharing trips and discovering recommendations is completely free in Stippl on web, iOS and Android.",
		},
		{
			question: "How do I find other travellers to follow on Stippl?",
			answer:
				"Search for friends by name or username, or discover new travellers through their published trips and shared places. Follow anyone whose recommendations you find interesting.",
		},
		{
			question: "Can I share my trip itinerary with the Stippl community?",
			answer:
				"Yes. Publish any trip itinerary to your Stippl profile and the community can browse it for inspiration. You control which trips are public and which stay private.",
		},
		{
			question: "Is my Stippl profile private?",
			answer:
				"Your profile is public by default so other travellers can discover your recommendations, but you control which trips and journal entries you share. Anything you don't publish stays visible only to you.",
		},
		{
			question: "Can I use Stippl as a travel social network?",
			answer:
				"Yes. Stippl works as a social network built specifically for travel: follow friends, share adventures, discover places through people you trust and build a record of your travel life in one place.",
		},
	],
};

export const TRAVEL_JOURNAL_CONTENT = {
	relatedFeatures: [
		{ label: "Travel reel", href: "/travel-reel", icon: "movie", color: "main4" },
		{ label: "Travel tracker", href: "/travel-tracker", icon: "globe", color: "main2" },
		{ label: "Social profile", href: "/social-travel-platform", icon: "userGroup", color: "main1" },
	],
	title: "The best way to relive your travel experiences",
	text: "Write about your adventures and upload your travel photos",
	cta: "Explore travel journals",
	icon: "addPhoto",
	feature: "Travel journals",
	usps: ["Document your travels", "Capture the highlights and memorable moments with your photos", "Share journals with your friends & family"],
	color: "main4",
	bgColor: "grey",
	featureList: TravelJournalFeatureContent,
	headerImage: [JournalHeaderDesktop, JournalHeaderMobile],
	imageAlt: "Stippl travel journal showing written entries and photos for a trip destination",
	pageTitle: "Travel Journals",
	textSection: {
		heading: "What is a digital travel journal?",
		paragraphs: [
			"A digital travel journal is a way to document your trips beyond photos: capturing the details, stories and personal moments that make each destination memorable. Unlike a photo album or social post, a travel journal gives you space to write about the experience: what surprised you, what you'd do differently, the places only locals know about.",
			"Stippl's travel journal feature is tied to your itinerary. Write entries for individual destinations, attach your photos to specific moments on the trip and keep everything organised within the trip that inspired it. Your journals build into a searchable personal travel archive you can revisit any time.",
			"When you're ready to share, Stippl lets you publish your travel journal to the Stippl community. Other travellers can discover your entries, get inspiration for their own trips and follow along on your adventures. Your personal notes become a resource for people planning the same destinations, and a record of your travels that goes well beyond a grid of photos.",
		],
	},
	faqItems: [
		{
			question: "Is Stippl's travel journal app free?",
			answer: "Yes. Writing and sharing travel journals in Stippl is completely free on web, iOS and Android.",
		},
		{
			question: "Can I add photos to my travel journal?",
			answer:
				"Yes. Upload photos directly to your journal entries and they appear alongside your written notes. Your journal becomes a combined photo and story record of the trip.",
		},
		{
			question: "Can I keep my travel journal private?",
			answer:
				"Yes. Journals are private by default. You choose when to publish an entry to the Stippl community, unpublished journals are only visible to you.",
		},
		{
			question: "Does Stippl's travel journal work offline?",
			answer: "Yes. Write journal entries offline in the Stippl mobile app and they sync automatically when you're back online.",
		},
		{
			question: "Can I use Stippl as a travel diary app?",
			answer:
				"Yes. Stippl's travel journal feature works as a digital travel diary: write destination-by-destination entries, add photos and build a lasting record of every trip.",
		},
	],
};

export const TRAVEL_TRACKER_CONTENT = {
	// seoTitle/seoDescription are the <title> and meta description, and deliberately not title/text:
	// this page's H1 comes from its i18n namespace, and title/text are only buildHead's fallback. One
	// value serving both roles is how /packing-list fell from 36,126 impressions to 661 (123yb692vrt).
	// Derived from the knowledge base — see the header.
	seoTitle: "Travel tracker app: map, journal and share it free | Stippl",
	seoDescription:
		"Track your trip on a live route map built from your itinerary, write a journal at every stop and share it with one link. Free, on web, iOS and Android.",
	title: "Track your journey. Share every moment.",
	text: "Stippl is a free travel tracker app and travel journal built into your trip planner. Map your route on a live tracker map, write photo journals from every stop, share your trip with anyone, and turn your journey into a hardcover photobook or travel reel.",
	pageTitle: "Travel Tracker",
	softwareApplication: {
		name: "Stippl Travel Tracker",
		description: "Free travel tracker app with live route map, photo travel journal, public trip sharing and photobook generation.",
	},
	howToUse: {
		name: "How to use Stippl as your travel tracker and journal app",
		description: "From trip planning to printed photobook, all in one app.",
	},
	howToSteps: [
		{
			name: "Create your trip",
			text: "Add your destinations and travel dates. Your trip tracker map is created automatically from your itinerary, every stop connected into a live route.",
		},
		{
			name: "Write journal entries on the go",
			text: "At each stop, write a travel journal entry and add photos. Your online travel journal builds up automatically and publishes live updates to anyone following your trip.",
		},
		{
			name: "Share your trip link",
			text: "Send your trip tracker link to anyone: they open it in any browser, follow your live map and read your travel journal in real time. No account or app download needed.",
		},
		{
			name: "Generate a photobook or reel",
			text: "When your journey ends, generate a printed photobook or animated travel reel from your journal in one click, the best way to relive and share every trip.",
		},
	],
	faqItems: [
		{
			question: "What is a travel tracker app?",
			answer:
				"A travel tracker app records your journey in real time: logging your route on a map, tracking each destination you visit, and letting you document your experience with photos and journal entries. Stippl is a free travel tracker app that combines a live trip tracker map, photo travel journal and trip sharing in one place, alongside your itinerary planner and budget tracker.",
		},
		{
			question: "Is Stippl a good alternative to Polarsteps?",
			answer:
				"Yes, Stippl is one of the most complete Polarsteps alternatives available. It offers the same core travel tracker and journal features: live route map, photo journal entries, public trip sharing and follower notifications. On top of that, Stippl adds a full trip itinerary planner, travel budget tracker, packing list, travel eSIM and the ability to generate a hardcover photobook or travel reel from your journals, all free.",
		},
		{
			question: "Can I share my travel journal with people who don't have Stippl?",
			answer:
				"Yes. Stippl generates a public trip link that anyone can open in any browser, no Stippl account or app download required. Friends and family can follow your live route on the trip tracker map, read your travel journal entries and see your photos in real time, from anywhere in the world.",
		},
		{
			question: "Can I turn my travel journal into a photobook?",
			answer:
				"Yes. Stippl can automatically generate a hardcover travel photobook from your travel journal with one click. Your photos, journal stories, route map and travel stats are laid out into a print-ready photobook, ordered directly in the app and delivered worldwide.",
		},
		{
			question: "What is the difference between a travel tracker, a trip tracker and a travel journal app?",
			answer:
				"A travel tracker app records where you go, mapping your route and logging each location. A trip tracker typically adds more granular detail: timing, distances and stop durations. A travel journal app adds the storytelling layer, written entries, photos and memories from each destination. Stippl combines all three: a live travel tracker map, a detailed trip tracker and a full photo travel journal, all built into a single free app.",
		},
	],
};

export const TRAVEL_REEL_CONTENT = {
	// seoTitle/seoDescription are the <title> and meta description, and deliberately not title/text:
	// this page's H1 comes from its i18n namespace, and title/text are only buildHead's fallback. One
	// value serving both roles is how /packing-list fell from 36,126 impressions to 661 (123yb692vrt).
	// Derived from the knowledge base — see the header.
	seoTitle: "Turn your trips into 3D travel reels. Share every journey.",
	seoDescription:
		"Generate a stunning 3D video of your entire route and photos, automatically. No editing, no software, no timeline. Plan a trip, add your photos, and your reel is ready in minutes.",
	title: "Turn your trips into 3D travel reels. Share every journey.",
	text: "Generate a stunning 3D video of your entire route and photos, automatically. No editing, no software, no timeline. Plan a trip, add your photos, and your reel is ready in minutes.",
	pageTitle: "Travel Reel",
	softwareApplication: {
		name: "Stippl Travel Reel",
		description: "Automatic 3D travel-video generator that turns trip itineraries and journal photos into shareable reels.",
	},
	howToUse: {
		name: "Your reel makes itself",
		description: "Fully automatic, from planned trip to shareable 3D video.",
	},
	howToSteps: [
		{
			name: "Plan your trip",
			text: "Add your stops and route in Stippl, that's all the reel needs to start.",
		},
		{
			name: "Add photos to your journal",
			text: "As you travel, or all at once after. More photos = a richer reel.",
		},
		{
			name: "Your reel generates itself",
			text: "3D route plus your photos, stitched automatically in minutes.",
		},
		{
			name: "Share it",
			text: "Export to Reels, TikTok, Shorts, or save it to your camera roll.",
		},
	],
	faqItems: [
		{
			question: "Is the Travel Reel feature free?",
			answer:
				"Yes. Generating, re-rendering and sharing Travel Reels is completely free in Stippl. Plan a trip, add photos to your journal, and create your reel at no cost. Pro adds extra customisation, but the reel itself is free.",
		},
		{
			question: "How does Stippl generate a Travel Reel automatically?",
			answer:
				"Your itinerary already holds the destinations and dates. Stippl combines them with the photos you upload to your journals. It animates your route on a 3D map and weaves your photos in at each stop. No editing, no software.",
		},
		{
			question: "Can I re-render my reel?",
			answer: "As many times as you like, unlimited re-renders are included on the free plan. Add photos, change the pace, and regenerate whenever you want.",
		},
		{
			question: "Can I control how my reel looks?",
			answer:
				"Yes. You can set the pace of your reel to speed it up or slow it down. With Stippl Pro you also unlock different map styles and custom colours to match your trip's vibe.",
		},
		{
			question: "Can I share my Travel Reel on Instagram or TikTok?",
			answer:
				"Yes. Reels are formatted for social. Download yours and post it to Instagram Reels, TikTok, YouTube Shorts, or anywhere, and tag @stippl.io to earn Stippl points toward rewards like free eSIMs.",
		},
		{
			question: "Do I need to add photos to get a Travel Reel?",
			answer:
				"No, you'll get a reel from your route alone. Adding your photos makes it personal (up to 10 per destination on Pro), and the more you add, the richer it becomes.",
		},
		{
			question: "How long is a Stippl Travel Reel?",
			answer:
				"It depends on how many destinations and photos your trip includes, and the pace you set. Most run between 30 seconds and a few minutes: long enough to tell the story, short enough to share.",
		},
	],
};

export const SCRATCH_MAP_CONTENT = {
	relatedFeatures: [
		{ label: "Travel tracker", href: "/travel-tracker", icon: "globe", color: "main2" },
		{ label: "Your map", href: "/your-map", icon: "map", color: "main4" },
		{ label: "Social profile", href: "/social-travel-platform", icon: "userGroup", color: "main1" },
	],
	title: "The most beautiful way to share your wanderlust",
	text: "Share the countries you've visited and the destinations on your wish-list",
	cta: "Explore the scratch map",
	icon: "scratchGlobe",
	feature: "scratch map",
	usps: [
		"Add all the countries you've visited",
		"Add the countries on your bucket list",
		"Customise your online scratch map",
		"Share your scratch maps on social media",
	],
	color: "main1",
	bgColor: "grey",
	featureList: ScratchMapFeatureContent,
	headerImage: [ScratchmapHeaderDesktop, ScratchmapHeaderMobile],
	pageTitle: "Scratch map",
	textSection: {
		heading: "What is an online scratch map?",
		paragraphs: [
			"An online scratch map is a digital version of the classic scratch-off travel map: you mark the countries and regions you've visited, building a visual record of your travels over time. Unlike the physical poster on the wall, a digital scratch map is always with you, easy to update after every trip and simple to share with anyone who wants to see where you've been.",
			"Stippl's scratch map lets you add countries as you visit them, mark destinations on your bucket list and customise how your map looks. Your visited countries, wishlist destinations and home country are colour-coded so you get an instant picture of your travel history and how much of the world is still to explore. It's a motivating way to track progress across a lifetime of travel.",
			"Sharing your scratch map is one of the most popular things travellers do with it. Post it to Instagram, send it to friends or embed it on your travel blog. A scratch map is a conversation starter: the moment someone sees yours, they want to know the story behind each visited country. It's a summary of your entire travel history in a single image.",
			"On Stippl, your scratch map connects to the rest of your travel profile. Countries you travel to show up in your itinerary history, and you can jump from a visited country on the map directly to the trips and journal entries from that destination. Your scratch map becomes an index of everywhere you've been and everything you did there.",
		],
	},
	faqItems: [
		{
			question: "Is Stippl's scratch map free?",
			answer: "Yes. Creating and sharing your online scratch map is completely free in Stippl on web, iOS and Android.",
		},
		{
			question: "How many countries can I add to my scratch map?",
			answer:
				"You can add all 195 countries in the world to your Stippl scratch map. Mark the ones you've visited, add the ones on your bucket list and watch the map fill up over time.",
		},
		{
			question: "Can I share my scratch map on social media?",
			answer:
				"Yes. Share your scratch map directly to Instagram, Twitter or any other platform. It's an easy way to show off your travel history and inspire other travellers.",
		},
		{
			question: "Does the scratch map work on mobile?",
			answer:
				"Yes. Your Stippl scratch map is fully accessible on iOS and Android. Update it after every trip from your phone and share it with friends on the go.",
		},
		{
			question: "Can I see other people's scratch maps on Stippl?",
			answer:
				"Yes. Scratch maps are visible on Stippl travel profiles. Follow other travellers to see their visited countries and get inspired by where they've been.",
		},
	],
};

export const ESIM_CONTENT = {
	// seoTitle/seoDescription are the <title> and meta description, and deliberately not title/text:
	// this page's H1 comes from its i18n namespace, and title/text are only buildHead's fallback. One
	// value serving both roles is how /packing-list fell from 36,126 impressions to 661 (123yb692vrt).
	// Derived from the knowledge base — see the header.
	seoTitle: "Your travel eSIM, built into your trip.",
	seoDescription:
		"Stippl puts a travel eSIM right inside your trip planner. Instant connectivity in 150+ countries, no roaming charges, no SIM swapping, and you can earn a free eSIM through Stippl points.",
	title: "Your travel eSIM, built into your trip.",
	text: "Stippl puts a travel eSIM right inside your trip planner. Instant connectivity in 150+ countries, no roaming charges, no SIM swapping, and you can earn a free eSIM through Stippl points.",
	pageTitle: "eSIM",
	softwareApplication: {
		name: "Stippl Travel eSIM",
		description: "Travel eSIM for instant mobile data in 150+ countries. Earn a free eSIM with Stippl points.",
		// eSIM ships on iOS only: Android renders an "available on iOS" placeholder and web has no
		// eSIM UI, so the product-wide platform and feature defaults do not apply to this page.
		operatingSystem: "iOS",
		// Real store URL, deliberately -- see jsonLd.ts's own comment above DEFAULT_DOWNLOAD_URLS
		// for why schema.org's `downloadUrl` keeps a canonical store link even though 123yb6937h3
		// moved every clickable download CTA on the site to an attributed AppsFlyer OneLink.
		downloadUrl: ["https://apps.apple.com/app/apple-store/id6443617088"],
		featureList: ["Travel eSIM purchase", "eSIM installation", "Mobile data usage tracking", "eSIM top-up"],
	},
	howToUse: {
		name: "How to get a travel eSIM with Stippl",
		description: "From trip creation to being connected on the ground, in four steps.",
	},
	howToSteps: [
		{
			name: "Create your trip",
			text: "Start a trip in Stippl and add your destinations and travel dates. Your travel eSIM options appear automatically based on where you're going.",
		},
		{
			name: "Choose your eSIM plan",
			text: "Compare travel eSIM plans for your destination, or select a multi-country plan if you're visiting several countries. See pricing, coverage and data allowances in one place.",
		},
		{
			name: "Activate before you fly",
			text: "Scan the QR code to install the eSIM for travel on your phone before you leave. Arrive at your destination and connect instantly, no airport SIM hunt required.",
		},
		{
			name: "Track data & earn points",
			text: "Monitor your data usage from within Stippl. Invite friends or book accommodations to earn Stippl points and redeem them for a free travel eSIM on your next trip.",
		},
	],
	faqItems: [
		{
			question: "How much does a travel eSIM cost on Stippl?",
			answer:
				"Stippl's travel eSIM plans are priced per destination and data allowance, typically a fraction of what carrier roaming costs. You can also earn a completely free travel eSIM by collecting Stippl points: invite friends to Stippl or book accommodations through the app, then redeem your points for a free eSIM with no payment required.",
		},
		{
			question: "How does a travel eSIM work?",
			answer:
				"A travel eSIM is a digital SIM card you activate on your phone before you travel: no physical card, no SIM tray required. With Stippl's travel eSIM, you scan a QR code, select your data plan and you're connected the moment you land. Your existing number stays active on your physical SIM at the same time. You can manage your active eSIM alongside your travel itinerary and budget in the Stippl app.",
		},
		{
			question: "What is the difference between an eSIM and a regular SIM for travel?",
			answer:
				"A regular travel SIM card requires you to physically swap out your home SIM, which means you temporarily lose your regular number. A travel eSIM runs digitally alongside your existing SIM: no swap needed, no number lost, no roaming charges. Stippl's eSIM for travel lets you activate connectivity instantly from your phone, then track spending with the built-in travel budget planner.",
		},
		{
			question: "Can I get a free eSIM with Stippl points?",
			answer:
				"Yes. Stippl has a Stippl points system where you earn points for actions like inviting friends to the app or booking accommodations through Stippl. Accumulated points can be redeemed for a free travel eSIM, no payment required.",
		},
		{
			question: "Does Stippl offer eSIMs for multiple destinations?",
			answer:
				"Yes. Stippl's travel eSIM covers 150+ countries and supports multi-destination trips. If you are travelling through several countries on one trip, you can manage your eSIM data across all destinations from a single dashboard inside the Stippl app, alongside your itinerary and budget tracker.",
		},
	],
};

export const PRO_CONTENT = {
	// seoTitle/seoDescription are the <title> and meta description, and deliberately not title/text:
	// this page's H1 comes from its i18n namespace, and title/text are only buildHead's fallback. One
	// value serving both roles is how /packing-list fell from 36,126 impressions to 661 (123yb692vrt).
	// Derived from the knowledge base — see the header.
	seoTitle: "Stippl Pro: unlimited AI trips & PRO hotel rates",
	seoDescription: "Go Pro for unlimited AI trips, PRO hotel pricing, PDF export and more. One plan, every Stippl feature unlocked.",
	title: "Travel better. Plan smarter.",
	text: "Trip planning, budget tracking, packing lists, travel journal: all free, forever. Pro adds the power features: AI-generated itineraries, PDF export, calendar sync and more.",
	pageTitle: "Pro Stippl",
	softwareApplication: {
		name: "Stippl Pro",
		description: "Unlock the full Stippl experience: AI planning, PDF export, calendar sync and more.",
	},
	faqItems: [
		{
			question: "What does Stippl Pro include?",
			answer:
				"Stippl Pro includes: unlimited AI trip and day-plan generation, PDF export, forward email reservations, calendar sync, expense export, custom budget categories, re-usable packing checklists, travel reel customisation (10 photos per destination), personalised map styles and a 20% discount on photobooks. All of this is in addition to the full free plan, which includes unlimited trips, budgets, journals, packing lists and travel tracking.",
		},
		{
			question: "How much does Stippl Pro cost?",
			answer:
				"Stippl Pro starts from €4.99 per month. The exact price may vary slightly depending on your country and currency. You can cancel at any time: no contracts, no hidden fees. Your Pro features remain active until the end of your billing period if you cancel.",
		},
		{
			question: "Can I cancel Stippl Pro?",
			answer:
				"Yes. You can cancel Stippl Pro at any time from your account settings. There are no cancellation fees. Your Pro features stay active until the end of your current billing month.",
		},
		{
			question: "Is Stippl available on iOS and Android?",
			answer: "Yes. Stippl is available on iOS (iPhone and iPad), Android and as a web app at stippl.io. Your trips sync across all devices automatically.",
		},
		{
			question: "Is Stippl Pro worth it?",
			answer:
				"For frequent travellers who want AI-generated itineraries, PDF export and calendar sync, yes. At €4.99/month, the biggest value driver is unlimited AI trip generation, a complete day-by-day itinerary for any destination in under 2 minutes. Stippl is rated 4.7 on the App Store by 8,000+ travellers. The full free plan remains available with no limits on trips, budgets or packing lists, so you can try everything before upgrading.",
		},
	],
};

export const PHOTOBOOK_CONTENT = {
	// seoTitle/seoDescription are the <title> and meta description, and deliberately not title/text:
	// this page's H1 comes from its i18n namespace, and title/text are only buildHead's fallback. One
	// value serving both roles is how /packing-list fell from 36,126 impressions to 661 (123yb692vrt).
	// Derived from the knowledge base — see the header.
	seoTitle: "Travel photo book: printed from your trip journal | Stippl",
	seoDescription: "Turn your Stippl journal into a printed travel photo book: hardcover, by destination, mailed to your door. Free to create, no subscription.",
	title: "Your trip. Printed. Beautifully.",
	text: "Journal your trip as you go. Get home. Hold your journey in your hands: photos, route map and all, in a high-quality printed travel photo book.",
	pageTitle: "Photobook",
	softwareApplication: {
		name: "Stippl Photobook",
		description: "Your travel journal, turned into a printed travel photobook.",
	},
	howToUse: {
		name: "How to create your travel photobook with Stippl",
		description: "Journal as you go. When you're home, your photobook is already waiting: photos, route map and all.",
	},
	howToSteps: [
		{
			name: "Journal your trip",
			text: "Write daily entries, add photos, and let Stippl track your route automatically. Every moment captured in the app is a page waiting to be printed.",
		},
		{
			name: "Create your photobook",
			text: "Open your journal, tap 'Create photobook', choose your content, and let Stippl generate the layout. No design skills needed, it's done automatically.",
		},
		{
			name: "Order or share",
			text: "Order a high-quality printed copy delivered to your door, or share your photobook digitally. Pro members receive a discount on every order.",
		},
	],
	faqItems: [
		{
			question: "What is a travel photobook?",
			answer:
				"A travel photobook is a printed book made from your trip photos, journal entries and memories, organised by day and destination. Unlike a traditional photo album, it can include your travel route, written reflections and trip statistics. Stippl's photobook is generated directly from your travel journal, with photos sorted by day, journal entries turned into captions and your actual GPS route displayed as a satellite map inside the book.",
		},
		{
			question: "Do I need Stippl Pro to create a photobook?",
			answer:
				"No. Travel photobooks are available to all Stippl users and do not require a Pro subscription. Stippl Pro members receive a 20% discount on every photobook order, along with additional benefits such as unlimited AI trip generation, PDF export and calendar sync.",
		},
		{
			question: "How long does it take to create a photobook?",
			answer:
				"Creating a photobook usually takes less than five minutes if your travel journal is already up to date. Because your photos, journal entries and route have been collected during your trip, most of the work is already done. Simply open your journal, select 'Create photobook', choose the content you want to include and Stippl automatically generates the layout.",
		},
	],
};
